import { InfoField } from "../InfoField";
import { ObjectIdentityTitle } from "../ObjectIdentityTitle";
import { REFERENCE_FRAME, withTimeSystem } from "../../constants/orbitMetadata";
import {
  getOrbitDisplayValue,
  getOrbitNestedDisplayValue,
} from "./orbitUiUtils";
import { formatDate } from "../events/eventEvolutionUtils";

interface OrbitSummaryCardProps {
  className?: string;
  orbit: unknown;
}

export const OrbitSummaryCard = ({
  className = "",
  orbit,
}: OrbitSummaryCardProps) => {
  const name = getOrbitNestedDisplayValue(orbit, ["spaceObject", "name"]);
  const norad = getOrbitNestedDisplayValue(orbit, ["spaceObject", "noradId"]);
  const cospar = getOrbitNestedDisplayValue(orbit, ["spaceObject", "cosparId"]);
  const operator = getOrbitNestedDisplayValue(orbit, [
    "operator",
    "value",
  ]).toUpperCase();
  const creationTime = getOrbitNestedDisplayValue(orbit, ["creationDate"]);
  const source = getOrbitNestedDisplayValue(orbit, ["sourceOfInformation"]);
  const category = getOrbitNestedDisplayValue(orbit, ["category"]);
  const startTime = getOrbitNestedDisplayValue(orbit, ["startDate"]);
  const endTime = getOrbitNestedDisplayValue(orbit, ["endDate"]);
  const originalFilename = getOrbitNestedDisplayValue(orbit, ["orbitFile"]);

  return (
    <aside
      className={`flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-none ${className}`}
    >
      <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <ObjectIdentityTitle
                className="min-w-0 max-w-full"
                cospar={getOrbitDisplayValue(cospar)}
                name={getOrbitDisplayValue(name)}
                norad={getOrbitDisplayValue(norad)}
              />
              {operator !== "-" && (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
                  {operator}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
              {`${withTimeSystem("Creation Time")}: ${formatDate(creationTime)}`}
            </span>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/40 p-5">
        <div className="flex min-h-0 flex-col gap-4">
          <section>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
              <InfoField label="Original filename" value={originalFilename} />
              <InfoField
                label="File metadata"
                value={
                  <span className="flex min-w-0 flex-wrap gap-x-3 gap-y-1">
                    <span>{`Source: ${source}`}</span>
                    <span>{`Category: ${category}`}</span>
                    <span>{`Reference frame: ${REFERENCE_FRAME}`}</span>
                  </span>
                }
              />
              <InfoField
                label={withTimeSystem("Start Time")}
                value={formatDate(startTime)}
              />
              <InfoField
                label={withTimeSystem("End Time")}
                value={formatDate(endTime)}
              />
            </div>
          </section>
        </div>
      </div>
    </aside>
  );
};
